/**
 * SUPERSEDED for the Rigs and Handheld pickers (2026-10-10): they use Ingest's
 * one picker, src/apps/ingest/views/IngestJobPicker.jsx, whose rule is
 * src/lib/ingestPicker.js over the server's slim list (listIngestJobs). What
 * still reads this file: an event account's list (RigV2App EventJobPick:
 * ingestJobList with eventAccount, unchanged) and the unrouted v1
 * CaptureArraysView.
 *
 * Which jobs Ingest's job pickers listed (Rigs, Handheld, and the unrouted v1
 * CaptureArraysView), in one place. Pure: no Firebase here; the reads behind
 * the capture-attempt signals are in ingestJobActivity.js.
 *
 * Why (Ivin, 2026-10-09): marking XR Brooklyn paid took it out of Ingest
 * while its guest pages were live. Paid, invoiced and delivered jobs are all
 * status "delivered" in the job model (migrateJob in utils.js folds the old
 * "invoiced" / "paid" statuses into the money flags), and the pickers listed
 * scheduled and processing jobs only. "Jobs may still be active once they are
 * paid."
 *
 * The rule: a job is listed when it is
 *   - scheduled, in progress or processing; or
 *   - delivered (invoiced and paid included) and still active, from what the
 *     job document says (registration open and captures not frozen, or a
 *     capture row fired in the last 30 days) or from its capture attempts
 *     (a guest delivery with pages not ended, captures in review, a capture
 *     not finished, or capture or delivery activity in the last 30 days).
 * A studio walk-ins job (Rigs' Quick guest, one per month: functions/src/
 * services/studioWalkins.ts) is listed as its status says during its month;
 * once its captures closed at the month's end (captureFrozenAt) it is listed
 * like a delivered job, only while still active, so past months do not pile
 * up in the list (Find any job still reaches every one).
 * A deleted job (deletedAt) is never listed, and never found by the search.
 * An event account's list is its assigned jobs, whatever their status, as
 * before (docs/ops/event-role.md).
 *
 * Left out, because the Suite cannot read it: the processing queue's tasks
 * (no client rule; the admin callable getProcessingQueue reads them) and the
 * guest delivery records themselves (server only). The capture attempt's
 * delivery mirror, which the server keeps in step, stands in for both.
 */

export const INGEST_LIVE_STATUSES = ["scheduled", "in_progress", "processing"];
export const ACTIVE_WINDOW_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;
export const activeCutoff = (now) => now - ACTIVE_WINDOW_DAYS * DAY_MS;

// The job's status as the job model reads it (migrateJob in utils.js): the
// legacy pre_scan is scheduled; invoiced and paid are delivered.
export function ingestStatusOf(job) {
  const s = job?.status;
  if (s === "pre_scan") return "scheduled";
  if (s === "invoiced" || s === "paid") return "delivered";
  return typeof s === "string" ? s : "";
}

export const isDeletedJob = (job) => Boolean(job?.deletedAt);
export const isLiveStatus = (job) => INGEST_LIVE_STATUSES.includes(ingestStatusOf(job));
export const isAfterDelivery = (job) => ingestStatusOf(job) === "delivered";

// The money stage of a delivered job, for the label: paid, invoiced or delivered.
export function stageOf(job) {
  const s = job?.status;
  if (s === "paid" || job?.paid === true) return "paid";
  if (s === "invoiced" || job?.invoiced === true) return "invoiced";
  return ingestStatusOf(job);
}

const STAGE_WORDS = {
  scheduled: "Scheduled", in_progress: "In progress", processing: "Processing",
  delivered: "Delivered", invoiced: "Invoiced", paid: "Paid",
};
export function stageLabel(job) {
  const st = stageOf(job);
  if (STAGE_WORDS[st]) return STAGE_WORDS[st];
  if (!st) return "No status";
  const words = st.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Every capture log row on the job document: the root log and each session's,
// and with withDeleted the rows moved to the deleted bins (captureLogDeleted).
function captureRows(job, { withDeleted = false } = {}) {
  const keys = withDeleted ? ["captureLog", "captureLogDeleted"] : ["captureLog"];
  const rows = [];
  for (const k of keys) {
    if (Array.isArray(job?.[k])) rows.push(...job[k]);
    if (Array.isArray(job?.sessions)) {
      for (const s of job.sessions) if (s && Array.isArray(s[k])) rows.push(...s[k]);
    }
  }
  return rows.filter((r) => r && typeof r === "object");
}

// When the job's captures were frozen (captureFrozenAt: ms or a Firestore
// timestamp), as functions/src/services/captureFreeze.ts reads it.
function frozenAt(job) {
  const v = job?.captureFrozenAt;
  if (typeof v === "number" && Number.isFinite(v) && v > 0) return v;
  if (v && typeof v === "object" && typeof v.toMillis === "function") {
    const ms = v.toMillis();
    return Number.isFinite(ms) && ms > 0 ? ms : null;
  }
  return null;
}

/**
 * A studio walk-ins job (functions/src/services/studioWalkins.ts): it carries
 * studioWalkins.month ("2026-10").
 */
export function isStudioWalkins(job) {
  const w = job?.studioWalkins;
  return Boolean(w && typeof w === "object" && !Array.isArray(w) && typeof w.month === "string" && /^\d{4}-\d{2}$/.test(w.month));
}

/** A past month's walk-in job: its captures closed (captureFrozenAt has come). */
export function isClosedWalkins(job, now) {
  if (!isStudioWalkins(job)) return false;
  const at = frozenAt(job);
  return at !== null && typeof now === "number" && at <= now;
}

/**
 * Activity the job document itself shows (no extra read): "registration"
 * while its registration form is open and its captures are not frozen;
 * "recent" when a capture row fired in the last 30 days; otherwise null.
 */
export function jobDocActivity(job, now) {
  const reg = job?.registration;
  const frozen = frozenAt(job);
  const open = reg && typeof reg === "object" && reg.state === "open"
    && typeof reg.consent?.text === "string" && reg.consent.text.trim().length > 0;
  if (open && !(frozen !== null && frozen <= now)) return "registration";
  const cutoff = activeCutoff(now);
  if (captureRows(job).some((r) => typeof r.firedAt === "number" && r.firedAt >= cutoff)) return "recent";
  return null;
}

// Whether the job ever had rig captures or guest registration, so its capture
// attempts are worth reading. Jobs without (most finished studio jobs) have none.
export function hasRigTraces(job) {
  if (job?.registration && typeof job.registration === "object") return true;
  if (job?.ingestSettings && typeof job.ingestSettings === "object") return true;
  // The lowercase spelling is a legacy row shape the job card also treats as
  // an ingest capture (src/apps/jobs/views/jobcard/CaptureLog.jsx).
  // A row deleted from the log still means the job had rig captures (and
  // guests whose pages may be live).
  return captureRows(job, { withDeleted: true }).some((r) => Boolean(r.captureAttemptId || r.captureattemptid));
}

/** Whether a job's capture attempts need reading to decide if it is listed. */
export function needsAttemptCheck(job, now) {
  return !isDeletedJob(job) && (isAfterDelivery(job) || isClosedWalkins(job, now)) && jobDocActivity(job, now) === null && hasRigTraces(job);
}

/**
 * Whether Ingest lists the job, and why: { show, reason }.
 * reason: "status" (scheduled / in progress / processing), "registration",
 * "delivery", "review", "capturing" or "recent" when listed; "deleted",
 * "quiet", "unchecked" (its attempts could not be read) or "other" when not.
 * attempt: what ingestJobActivity found for this job, { reason, failed }, or
 * undefined (not read, or still reading).
 */
export function ingestVisibility(job, { now, attempt } = {}) {
  if (isDeletedJob(job)) return { show: false, reason: "deleted" };
  // A past month's walk-in job goes by its activity, whatever its status.
  const closedWalkins = isClosedWalkins(job, now);
  if (isLiveStatus(job) && !closedWalkins) return { show: true, reason: "status" };
  if (!isAfterDelivery(job) && !closedWalkins) return { show: false, reason: "other" };
  const doc = jobDocActivity(job, now);
  if (doc) return { show: true, reason: doc };
  if (attempt?.reason) return { show: true, reason: attempt.reason };
  return { show: false, reason: attempt?.failed ? "unchecked" : "quiet" };
}

/**
 * ingestVisibility for a picker that may still be reading attempts: a job
 * whose attempts are being read, and that nothing else lists yet, says
 * "checking" rather than "not active"; when this account does not read
 * attempts at all (reading false), "activity not checked".
 */
export function checkingVisibility(job, { now, activity = {}, checking = false, reading = true } = {}) {
  const vis = ingestVisibility(job, { now, attempt: activity[job?.id] });
  if (vis.show || activity[job?.id] || !needsAttemptCheck(job, now)) return vis;
  if (!reading) return { show: false, reason: "unchecked" };
  if (checking) return { show: false, reason: "checking" };
  return vis;
}

// Who reads capture attempts under the nyc-suite rules (superadmin, admin,
// crew; an event account only its own jobs, and its list does not need
// them). The Suite profile's role (users/{uid}.role) stands in for the rules'
// allow-list role; anyone else (a viewer, a blank role) is not asked to read.
export const canReadAttempts = (user) => ["superadmin", "admin", "crew"].includes(user?.role);

export const byJobName = (label) => (a, b) => label(a).localeCompare(label(b));
const defaultLabel = (j) => j.displayName ?? j.id;

/**
 * The list an Ingest picker shows. An event account: its assigned jobs as
 * they come, whatever their status (unchanged). Everyone else: the jobs
 * ingestVisibility shows. activity: { [jobId]: { reason, failed } }.
 */
export function ingestJobList(jobs, { now, activity = {}, eventAccount = false, label = defaultLabel } = {}) {
  const list = Array.isArray(jobs) ? jobs : [];
  // (An event account reads rigJobs/{id}, which carries no deletedAt, so the
  // filter here only matters if one ever does: a trashed job stays on an
  // event account's list until its grant is taken away, as before.)
  if (eventAccount) return list.filter((j) => !isDeletedJob(j)).sort(byJobName(label));
  return list.filter((j) => ingestVisibility(j, { now, attempt: activity[j.id] }).show).sort(byJobName(label));
}

const REASON_WORDS = {
  registration: "registration open",
  delivery: "guest pages live",
  review: "captures in review",
  capturing: "capture not finished",
  recent: `active in the last ${ACTIVE_WINDOW_DAYS} days`,
  quiet: "not active",
  unchecked: "activity not checked",
  checking: "checking activity",
};

/**
 * The words beside a job in the search results and on a delivered job's row:
 * "Scheduled", "Paid · guest pages live", "Paid · not active".
 */
export function ingestTag(job, vis) {
  const stage = stageLabel(job);
  if (!vis || vis.reason === "status" || vis.reason === "other" || vis.reason === "deleted") return stage;
  const words = REASON_WORDS[vis.reason];
  return words ? `${stage} · ${words}` : stage;
}

const norm = (s) => (typeof s === "string" ? s : "").toLowerCase();

/**
 * "Find any job": the jobs whose name, client, project or job id contain
 * every word typed (case does not matter), any status, deleted jobs never.
 * Two characters at least; at most `max` results, by name.
 */
export function findJobs(jobs, term, { max = 20, label = defaultLabel } = {}) {
  const words = norm(term).trim().split(/\s+/).filter(Boolean);
  if (words.join("").length < 2) return [];
  const list = Array.isArray(jobs) ? jobs : [];
  return list
    .filter((j) => j && !isDeletedJob(j))
    .filter((j) => {
      const hay = [j.displayName, j.clientName, j.project, j.jobId, j.id].map(norm).join(" ");
      return words.every((w) => hay.includes(w));
    })
    .sort(byJobName(label))
    .slice(0, max);
}
